import { SheetLayoutData, SheetLayout, FitStyle, ScrollBase } from '../utils/view';
import { Publication, SpineItem, PageSpread } from '../types/epub';
import { getDefaultRendition, toOPFBasedPath, getReflowableRendition } from '../utils/epub';
import { TabType, TabInfo, PagesState, isPopupPanelID } from '../reducers/pages';
import { Dimension } from '../types/common';

const toSheetLayoutData = (
  item: SpineItem,
  pair: boolean,
  enableMargin: boolean,
  frameSize: Dimension | undefined,
): SheetLayoutData => {
  if (item.fxl) {
    const common = {
      enableMargin,
      layout: SheetLayout.Fixed as SheetLayout.Fixed,
      fitStyle: item.fitStyle,
      basePosition: item.scrollBase,
    };

    if (pair) {
      if (frameSize !== undefined) {
        throw new Error('Setting frameContentSize in two-page spread is not supported.');
      }
      return {
        ...common,
        sheetWidth: item.width * 2,
        sheetHeight: item.height,
      };
    }

    if (frameSize !== undefined) {
      return {
        ...common,
        sheetWidth: frameSize.width,
        sheetHeight: frameSize.height,
      };
    }
    return {
      ...common,
      sheetWidth: item.width,
      sheetHeight: item.height,
    };
  }

  if (item.fitStyle === FitStyle.FitScreen) {
    return { layout: SheetLayout.Reflowable, fitStyle: item.fitStyle }; // Zooming disabled
  }

  return {
    enableMargin,
    blockFlowLength: getBlockFlowLengthForReflowableItem(item.fitStyle, frameSize),
    layout: SheetLayout.Reflowable,
    fitStyle: item.fitStyle,
  };
};

const getBlockFlowLengthForReflowableItem = (
  fitStyle: FitStyle.FitWidth | FitStyle.FitHeight,
  frameSize: Dimension | undefined,
): number => {
  if (frameSize === undefined) {
    return 0;
  }
  switch (fitStyle) {
    case FitStyle.FitWidth:
      return frameSize.height;
    case FitStyle.FitHeight:
      return frameSize.width;
  }
};

export const getSheetLayoutData = (
  publication: Readonly<Publication>,
  pages: Readonly<PagesState>,
  frameSize: Dimension | undefined,
  panelID: string,
): SheetLayoutData => {
  const tab = pages.tabs.filter(t => t.id === panelID)[0];
  if (tab !== undefined) {
    return getSheetLayoutDataForTab(publication, tab, frameSize);
  }

  if (isPopupPanelID(panelID)) {
    if (panelID in pages.popups) {
      return getSheetLayoutDataFromPath(publication, pages.popups[panelID].path, false, frameSize);
    }
    throw new Error('Illegal state: popup layout required, but no popups are opened');
  }

  throw new Error(`Cannot get sheet layout data for panelID ${panelID}`);
};

const getSheetLayoutDataFromPath = (
  publication: Readonly<Publication>,
  fullpath: string,
  enableMargin: boolean,
  frameSize: Dimension | undefined,
): SheetLayoutData => {
  const item = getDefaultRendition(publication).itemIndices[fullpath];
  return toSheetLayoutData(item, false, enableMargin, frameSize);
};

export const getSheetLayoutDataForTab = (
  publication: Readonly<Publication>,
  tabInfo: Readonly<TabInfo>,
  frameSize: Dimension | undefined,
): SheetLayoutData => {
  if (tabInfo.type === TabType.LinearPages) {
    const item = getDefaultRendition(publication).linearItems[tabInfo.currentPage];
    // Ensure doubled width unless the item is centered: regardless of opposing page existence.
    const twoPageSpread = item.pageSpread !== PageSpread.Center;
    return toSheetLayoutData(item, twoPageSpread, true, frameSize);
  }

  if (tabInfo.type === TabType.ExtraPage) {
    const fullpath = toOPFBasedPath(publication, tabInfo.path);
    const item = getDefaultRendition(publication).itemIndices[fullpath];
    if (item === undefined) {
      throw new Error(`'Illegal state: the spine item data is not found: ${fullpath}`);
    }
    return toSheetLayoutData(item, false, item.fxl, frameSize);
  }

  if (tabInfo.type === TabType.ReflowablePages) {
    const item = getReflowableRendition(publication).linearItems[tabInfo.currentPage];
    return toSheetLayoutData(item, false, item.fxl, frameSize);
  }

  if (tabInfo.type === TabType.GeneratedContent && tabInfo.params !== null) {
    // 動的生成固定レイアウトコンテンツを想定
    return {
      layout: SheetLayout.Fixed,
      sheetWidth: tabInfo.params['width'],
      sheetHeight: tabInfo.params['height'],
      fitStyle: FitStyle.FitScreen,
      basePosition: ScrollBase.Center,
      enableMargin: true,
    };
  }

  // XXX : Assumes that generated contents are horizontal-tb reflowable.
  return {
    layout: SheetLayout.Reflowable,
    enableMargin: false,
    fitStyle: FitStyle.FitWidth,
    blockFlowLength: frameSize ? frameSize.height : 0,
  };
};
